import { createRouter, createWebHistory } from 'vue-router';
import HomepageFirstPage from '@/public/views/FrontPage.vue';
import Cases from "@/public/views/Cases.vue";
import ProductPage from '@/public/views/Produkter.vue';
import AboutUs from "@/public/views/AboutUs.vue";
import ContactPage from '@/public/views/Contact.vue';
import AccessDenied from '@/public/components/auth/AccessDenied.vue';
import LoggedInHomePage from '@/app/views/LoggedInHomePage.vue'
import LoginForm from '@/public/components/auth/LoginForm.vue';
import Dashboard from '@/app/views/DashBoard.vue';
import SalesForecast from '@/app/views/SalesForecast.vue';
import Reviews from '@/app/views/Reviews.vue';
import UserProfile from '@/app/views/UserProfile.vue';
import SettingsPage from '@/app/views/SettingsPage.vue';
import AdminPage from '@/app/views/AdminPage.vue';
import store from '@/utils/store.js';
import ResetPassword from '@/public/components/auth/ResetPassword.vue';

const routes = [
  {
    path: '/',
    name: 'HomepageFirstPage',
    component: HomepageFirstPage
  },
  {
    path: '/home',
    name: 'LoggedInHomePage',
    component: LoggedInHomePage,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'], 
    }
  },
  {
    path: '/om-databite',
    name: 'AboutUs',
    component: AboutUs,
  },
  {
    path: "/cases",
    name: "Cases",
    component: Cases,
  },
  {
    path: '/kontakt',
    name: 'Contact',
    component: ContactPage
  },
  {
    path: '/produkter',
    name: 'ProductPage',
    component: ProductPage
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginForm
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'], 
    }
  },
  {
    path: '/forecast',
    name: 'Forecast',
    component: SalesForecast,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'],
    }
  },
  {
    path: '/reviews',
    name: 'Reviews',
    component: Reviews,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'], 
    }
  },
  {
    path: '/user',
    name: 'UserProfile',
    component: UserProfile,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'], 
    }
  },
  {
    path: '/settings',
    name: 'SettingsPage',
    component: SettingsPage,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'], 
    }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: AccessDenied,
  },
  {
    path: '/admin',
    name: 'AdminPage',
    component: AdminPage,
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner'],
      adminOnly: true
    }
  },
  {
    path: '/business/:businessName',
    name: 'BusinessOverview',
    component: () => import('@/app/views/BusinessOverview.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'],
    },
    children: [
      {
        path: '',
        name: 'BusinessDashboard',
        component: () => import('@/app/views/BusinessOverview.vue')
      },
      {
        path: 'forecast-performance',
        name: 'ForecastPerformance',
        component: () => import('@/app/views/ForecastPerformance.vue')
      },
      {
        path: 'menu-analysis',
        name: 'MenuAnalysis',
        component: () => import('@/app/views/MenuAnalysis.vue')
      },
      {
        path: 'sales',
        name: 'BusinessSales',
        component: () => import('@/app/views/BusinessSalesPage.vue')
      },
      {
        path: 'standard-dashboard',
        name: 'DashBoard',
        component: () => import('@/app/views/DashBoard.vue')
      },
      {
        path: 'nps-analysis',
        name: 'NPSAnalysis',
        component: () => import('@/app/views/NPSAnalysis.vue')
      },
      {
        path: 'daily-sales',
        name: 'DailySales',
        component: () => import('@/app/views/DailySales.vue')
      }
    ],
    beforeEnter: async (to, from, next) => {
      const businessName = to.params.businessName;
      const userBusinesses = store.state.organizations.flatMap(org => 
        org.businesses.map(business => business.data.businessName)
      );
      
      if (userBusinesses.includes(businessName)) {
        next();
      } else {
        next('/access-denied');
      }
    }
  },
  {
    path: '/rapporter',
    name: 'Reports',
    component: () => import('@/app/views/ReportsHub.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'],
    }
  },
  {
    path: '/business',
    name: 'Businesses',
    component: () => import('../app/views/BusinessesPage.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['admin', 'owner', 'user'],
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    props: route => ({ 
      oobCode: route.query.oobCode,
      mode: route.query.mode 
    })
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  await store.dispatch('checkUser');
  const isAuthenticated = store.state.user !== null;
  const userRole = store.state.role;
  const roles = to.meta.roles || [];

  if (requiresAuth && !isAuthenticated) {
    next('/login');
  } else if (requiresAuth && roles.length > 0 && !roles.includes(userRole)) {
    next('/access-denied');
  } else {
    next();
  }
});

router.afterEach((to) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: to.fullPath,
      page_title: document.title
    });
  }
});

export default router;
